import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import SystemAlertBar from '../../../platform/shared/components/SystemAlertBar';
import GET_ACTIVE_SYSTEM_ALERTS from '../../GraphQl/Queries/GET_ACTIVE_SYSTEM_ALERTS';
import PropTypes from 'prop-types';

const MAX_VISIBLE_ALERTS = 1;
const DISMISSED_ALERTS_KEY = 'dismissedSystemAlerts_session';

const SystemAlertBarContainer = ({ currentUser }) => {
  // Load dismissed alerts from sessionStorage on mount
  // sessionStorage automatically clears when browser closes (same session = same dismissals)
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    try {
      const stored = sessionStorage.getItem(DISMISSED_ALERTS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Update now timestamp on a regular interval to ensure fresh data
  // This ensures alerts scheduled for future times will appear when their startDate is reached
  const [now, setNow] = useState(new Date().toISOString());

  useEffect(() => {
    // Update the current time every minute (60 seconds)
    // This is faster than pollInterval (5 minutes) to catch scheduled alerts sooner
    const interval = setInterval(() => {
      setNow(new Date().toISOString());
    }, 60000); // 1 minute

    return () => clearInterval(interval);
  }, []);

  const { data, loading, error } = useQuery(GET_ACTIVE_SYSTEM_ALERTS, {
    variables: { now },
    fetchPolicy: 'cache-and-network', // Return cached data instantly, then fetch fresh data in background
    pollInterval: 300000, // Poll every 5 minutes (balanced between responsiveness and server load)
    skip: !currentUser, // Don't fetch until user is authenticated
    onError: (err) => {
      console.error('[SystemAlertBarContainer] GraphQL Error Details:', {
        message: err.message,
        graphQLErrors: err.graphQLErrors,
        networkError: err.networkError,
        variables: { now },
      });
    },
  });

  const { visibleAlerts, hiddenCount } = useMemo(() => {
    if (!data || !data.systemAlerts) {
      return { visibleAlerts: [], hiddenCount: 0 };
    }

    // Type priority: ERROR > WARNING > SUCCESS > INFO
    const typePriority = {
      ERROR: 4,
      WARNING: 3,
      SUCCESS: 2,
      INFO: 1,
    };

    // Filter out dismissed alerts and sort by type, then by creation date
    const activeAlerts = data.systemAlerts
      .filter((alert) => !dismissedAlerts.includes(alert.id))
      .sort((a, b) => {
        // First sort by type priority (ERROR always on top)
        const typeDiff = typePriority[b.type] - typePriority[a.type];
        if (typeDiff !== 0) return typeDiff;

        // Then sort by priority number (higher priority first)
        const priorityDiff = b.priority - a.priority;
        if (priorityDiff !== 0) return priorityDiff;

        // Finally sort by creation date (newest first)
        return new Date(b.startDate) - new Date(a.startDate);
      });

    // Limit to MAX_VISIBLE_ALERTS
    const visible = activeAlerts.slice(0, MAX_VISIBLE_ALERTS);
    const hidden = activeAlerts.length - visible.length;

    return {
      visibleAlerts: visible,
      hiddenCount: hidden > 0 ? hidden : 0,
    };
  }, [data, dismissedAlerts]);

  const handleDismiss = (alertId) => {
    // Session-scoped dismissal using sessionStorage
    // Persists across page refreshes & redirects within same browser session
    // Automatically clears when browser tab/window closes or new browser session starts
    setDismissedAlerts((prev) => {
      const updated = [...prev, alertId];
      try {
        sessionStorage.setItem(DISMISSED_ALERTS_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('[SystemAlertBarContainer] Error saving dismissed alerts to sessionStorage:', error);
      }
      return updated;
    });
  };

  if (loading || error || !currentUser) {
    return null;
  }

  return <SystemAlertBar alerts={visibleAlerts} hiddenCount={hiddenCount} onDismiss={handleDismiss} />;
};

SystemAlertBarContainer.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.string,
    username: PropTypes.string,
  }),
};

export default SystemAlertBarContainer;
